import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import AppAnalytics from '../analytics/AppAnalytics';

@customElement('train-lora-form')
export class TrainLoraForm extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .form-container {
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
      border-radius: 1rem;
      padding: 2rem;
      transform: hover:scale-[1.02];
      transition: all 0.3s;
      max-width: 48rem;
      margin: 0 auto;
    }

    @media (min-width: 768px) {
      .form-container {
        padding: 3rem;
      }
    }

    .form-container:hover {
      transform: scale(1.02);
    }

    .upload-section {
      margin-bottom: 2.5rem;
    }

    .label {
      display: block;
      font-size: 1.125rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      margin-bottom: 1rem;
    }

    .upload-area {
      border: 2px dashed rgba(0, 212, 255, 0.5);
      border-radius: 0.75rem;
      padding: 2.5rem;
      text-align: center;
      transition: all 0.2s;
      cursor: pointer;
    }

    .upload-area:hover {
      border-color: #00d4ff;
    }

    .upload-emoji {
      font-size: 3.75rem;
      margin-bottom: 1.5rem;
      animation: float 6s ease-in-out infinite;
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }

    .upload-text {
      color: rgba(226, 232, 240, 0.8);
      margin-bottom: 1.5rem;
      font-family: 'JetBrains Mono', monospace;
      font-size: 1.125rem;
    }

    .upload-spec {
      font-size: 0.875rem;
      color: rgba(226, 232, 240, 0.6);
      margin-bottom: 1.5rem;
      font-family: 'JetBrains Mono', monospace;
    }

    .upload-button {
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-image: linear-gradient(to right, #0ea5e9, #00d4ff);
      color: #0a0f1b;
      padding: 0.75rem 2rem;
      border-radius: 0.5rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.2s;
    }

    .upload-button:hover {
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      transform: scale(1.05);
    }

    .input-section {
      margin-bottom: 2.5rem;
    }

    .input {
      width: 100%;
      padding: 1rem 1.5rem;
      border-radius: 0.75rem;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-color: rgba(26, 29, 41, 0.5);
      color: #e2e8f0;
      font-family: 'JetBrains Mono', monospace;
      transition: all 0.2s;
    }

    .input:focus {
      outline: none;
      border-color: #00d4ff;
      box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.2);
    }

    .style-section {
      margin-bottom: 2.5rem;
    }

    .style-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    @media (min-width: 640px) {
      .style-grid {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    .style-option {
      position: relative;
    }

    .style-input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    .style-label {
      display: block;
      text-align: center;
      padding: 1.5rem;
      border-radius: 0.75rem;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      cursor: pointer;
      transition: all 0.2s;
      color: rgba(226, 232, 240, 0.6);
    }

    .style-input:checked + .style-label {
      border-color: #00d4ff;
      background: rgba(0, 212, 255, 0.1);
      color: #00d4ff;
    }

    .style-label:hover {
      border-color: rgba(0, 212, 255, 0.5);
    }

    .style-emoji {
      font-size: 2rem;
      margin-bottom: 0.75rem;
      animation: float 6s ease-in-out infinite;
    }

    .style-text {
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
    }

    .pricing-section {
      border-radius: 1rem;
      background: linear-gradient(135deg,
        rgba(0, 212, 255, 0.1) 0%,
        rgba(168, 85, 247, 0.1) 50%,
        rgba(236, 72, 153, 0.1) 100%);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 212, 255, 0.3);
      padding: 2rem;
      margin-bottom: 2.5rem;
      text-align: center;
    }

    .pricing-label {
      font-size: 0.875rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: 500;
      color: #00ff88;
      margin-bottom: 0.75rem;
    }

    .pricing-amount {
      font-size: 2.5rem;
      font-weight: bold;
      font-family: 'JetBrains Mono', monospace;
      color: #00d4ff;
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      margin-bottom: 0.75rem;
    }

    .pricing-description {
      font-size: 0.875rem;
      color: rgba(226, 232, 240, 0.7);
      font-family: 'JetBrains Mono', monospace;
    }

    .submit-button {
      width: 100%;
      border: 2px solid transparent;
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(168, 85, 247, 0.1)) padding-box,
                  linear-gradient(135deg, #00d4ff, #a855f7) border-box;
      background-image: linear-gradient(to right, #00ff88, #0ea5e9);
      color: #0a0f1b;
      padding: 1.25rem;
      border-radius: 0.75rem;
      font-family: 'JetBrains Mono', monospace;
      font-weight: bold;
      font-size: 1.25rem;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
    }

    .submit-button:hover {
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
      transform: scale(1.05);
    }

    .cyber-green { color: #00ff88; }
    .cyber-yellow { color: #ffd700; }
    .cyber-purple { color: #a855f7; }
    .cyber-pink { color: #ec4899; }
  `;

  private handleStartTraining() {
    AppAnalytics.getPlausible()?.trackEvent('cta_clicked', {
      props: {
        page: 'train-lora',
        button: 'start_training',
        location: 'main_form'
      }
    });

    const emailModal = document.querySelector('email-modal');
    if (emailModal) {
      (emailModal as any).source = 'train_lora_page';
      (emailModal as any).open = true;
      (emailModal as any).setAttribute('open', 'true');
      (emailModal as any).requestUpdate();
    }
  }

  render() {
    return html`
      <div class="form-container">
        <!-- Upload Section -->
        <div class="upload-section">
          <label class="label">
            <span class="cyber-green">[</span>UPLOAD_TRAINING_IMAGES<span class="cyber-green">]</span>
          </label>
          <div class="upload-area">
            <div class="upload-emoji">📸</div>
            <p class="upload-text">
              <span class="cyber-yellow">[ACTION]</span> Drag and drop your images here, or click to select files
            </p>
            <p class="upload-spec">
              <span class="cyber-green">[SPEC]</span> Recommended: 10-20 high-quality images (1024x1024 or higher)
            </p>
            <button class="upload-button">
              SELECT_IMAGES
            </button>
          </div>
        </div>

        <!-- Model Configuration -->
        <div class="input-section">
          <label class="label">
            <span class="cyber-pink">[</span>MODEL_NAME<span class="cyber-pink">]</span>
          </label>
          <input type="text" class="input" placeholder="ENTER_MODEL_NAME">
        </div>

        <div class="style-section">
          <label class="label">
            <span class="cyber-purple">[</span>TRAINING_STYLE<span class="cyber-purple">]</span>
          </label>
          <div class="style-grid">
            <div class="style-option">
              <input type="radio" id="style-character" name="style" value="character" class="style-input">
              <label for="style-character" class="style-label">
                <div class="style-emoji">👤</div>
                <div class="style-text">CHARACTER</div>
              </label>
            </div>
            <div class="style-option">
              <input type="radio" id="style-object" name="style" value="object" class="style-input">
              <label for="style-object" class="style-label">
                <div class="style-emoji" style="animation-delay: 0.2s;">🏺</div>
                <div class="style-text">OBJECT</div>
              </label>
            </div>
            <div class="style-option">
              <input type="radio" id="style-concept" name="style" value="concept" class="style-input">
              <label for="style-concept" class="style-label">
                <div class="style-emoji" style="animation-delay: 0.4s;">🎨</div>
                <div class="style-text">STYLE/CONCEPT</div>
              </label>
            </div>
          </div>
        </div>

        <!-- Pricing Display -->
        <div class="pricing-section">
          <div class="pricing-label">
            <span class="cyber-yellow">[</span>TRAINING_COST<span class="cyber-yellow">]</span>
          </div>
          <div class="pricing-amount">20 Credits</div>
          <div class="pricing-description">
            Includes 1000 training steps + model download
          </div>
        </div>

        <!-- Start Training Button -->
        <button
          class="submit-button"
          @click=${this.handleStartTraining}>
          <span>🚀</span>START_TRAINING_NOW
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'train-lora-form': TrainLoraForm;
  }
}
